// src/pages/DevHub/DevMail/DevMailPage.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Mail, Trash2, Search, Info, RefreshCw, Pause, Play,
    Send, Check, Eye, Copy, Zap, Image as ImageIcon, Smartphone, Monitor, Code
} from 'lucide-react';
import SEO from '../../../components/SEO/SEO';
import CodeBlock from '../../../components/CodeBlock/CodeBlock';
import {
    DevPageLayout,
    DevPanel,
} from '../components';

interface Email {
    id: string;
    from: string;
    to: string;
    subject: string;
    text: string;
    html: string;
    timestamp: number;
}

interface MailScenario {
    label: string;
    icon: React.ReactNode;
    payload: Partial<Email>;
}

const MailDocs: React.FC = () => (
    <div className="mt-[48px] pt-[32px] border-t border-[#dadce0]">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-[32px] lg:gap-[48px]">
            <div>
                <h3 className="text-[1.125rem] font-semibold mb-[16px] text-[#202124]">How it works</h3>
                <ul className="list-none p-0 text-[#5f6368] text-[14px] leading-relaxed">
                    <li className="mb-[12px]">1. Copy your unique sandbox URL.</li>
                    <li className="mb-[12px]">2. Configure your app to send transactional mail via JSON POST.</li>
                    <li className="mb-[12px]">3. Inspect the rendered HTML, attachments, and metadata here.</li>
                </ul>
                {/* .dev-alert: flex gap-[12px] p-[16px] bg-[#e8f0fe] text-[#1557b0] rounded-[8px] text-[14px] leading-[1.5] */}
                <div className="flex gap-[12px] p-[16px] bg-[#e8f0fe] text-[#1557b0] rounded-[8px] text-[14px] leading-[1.5] mt-[24px]">
                    <Info size={20} className="flex-shrink-0" />
                    <p><strong>Private:</strong> Your sandbox is uniquely identified by your Developer ID.</p>
                </div>
            </div>
            <div>
                <h3 className="text-[1.125rem] font-semibold mb-[16px] text-[#202124]">🚀 Advanced Testing</h3>
                <p className="text-[#80868b] text-[14px] mb-[12px] font-medium">Perfect your transactional flow:</p>
                <ul className="list-none p-0 text-[13px]">
                    <li className="mb-[12px] text-[#5f6368]"><strong className="text-[#202124]">HTML Preview</strong>: Toggle between Desktop &amp; Mobile views.</li>
                    <li className="mb-[12px] text-[#5f6368]"><strong className="text-[#202124]">Metadata</strong>: Inspect raw headers and MIME structures.</li>
                    <li className="mb-[12px] text-[#5f6368]"><strong className="text-[#202124]">Payloads</strong>: Test with dynamic JSON templates.</li>
                </ul>
            </div>
        </div>
    </div>
);

const DevMailPage: React.FC = () => {
    const apiBase = (import.meta as any).env.VITE_API_URL || 'http://localhost:8000';
    const [mailboxId, setMailboxId] = useState<string>('');
    const [emails, setEmails] = useState<Email[]>([]);
    const [activeEmail, setActiveEmail] = useState<Email | null>(null);
    const [isPolling, setIsPolling] = useState<boolean>(true);
    const [searchTerm, setSearchTerm] = useState<string>('');

    // Adaptive Polling
    const [isTabVisible, setIsTabVisible] = useState<boolean>(true);
    const [pollInterval, setPollInterval] = useState<number>(3000);
    const [lastMailCount, setLastMailCount] = useState<number>(0);
    const [isSendingTest, setIsSendingTest] = useState<boolean>(false);
    const [dispatchStatus, setDispatchStatus] = useState<'success' | 'error' | null>(null);

    const filteredEmails = emails.filter(email => {
        const query = searchTerm.toLowerCase();
        return (
            email.subject.toLowerCase().includes(query) ||
            email.from.toLowerCase().includes(query) ||
            email.to.toLowerCase().includes(query) ||
            email.text?.toLowerCase().includes(query) ||
            email.html?.toLowerCase().includes(query)
        );
    });

    useEffect(() => {
        const handleVisibility = () => setIsTabVisible(document.visibilityState === 'visible');
        document.addEventListener('visibilitychange', handleVisibility);
        return () => document.removeEventListener('visibilitychange', handleVisibility);
    }, []);

    useEffect(() => {
        const id = localStorage.getItem('ff_dev_id') || localStorage.getItem('ff_webhook_id');
        if (id) {
            setMailboxId(id);
            if (!localStorage.getItem('ff_dev_id')) localStorage.setItem('ff_dev_id', id);
        }
    }, []);

    const handleGenerateId = () => {
        const id = Math.random().toString(36).substring(2, 12);
        localStorage.setItem('ff_dev_id', id);
        setMailboxId(id);
    };

    const sendUrl = `${apiBase}/api/mail-sandbox/send/${mailboxId}`;

    const fetchMail = useCallback(async () => {
        if (!mailboxId) return;
        try {
            const res = await fetch(`${apiBase}/api/mail-sandbox/inbox/${mailboxId}`);
            const data = await res.json();
            if (Array.isArray(data)) {
                if (data.length > lastMailCount) { setPollInterval(3000); } else { setPollInterval(prev => Math.min(prev + 3000, 30000)); }
                setLastMailCount(data.length);
                setEmails(data);
            }
        } catch (err) { console.error('Failed to fetch mail:', err); }
    }, [mailboxId, lastMailCount, apiBase]);

    useEffect(() => { if (mailboxId) fetchMail(); }, [mailboxId, fetchMail]);

    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (isPolling && isTabVisible && mailboxId) interval = setInterval(fetchMail, pollInterval);
        return () => clearInterval(interval);
    }, [isPolling, isTabVisible, mailboxId, fetchMail, pollInterval]);

    const clearInbox = async () => {
        if (!window.confirm('Clear all emails in this sandbox?')) return;
        try {
            await fetch(`${apiBase}/api/mail-sandbox/inbox/${mailboxId}`, { method: 'DELETE' });
            setEmails([]); setActiveEmail(null); setLastMailCount(0);
        } catch (err) { console.error('Failed to clear inbox:', err); }
    };

    const formatTime = (ts: number) =>
        new Date(ts * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

    const [activeTab, setActiveTab] = useState<'javascript' | 'python' | 'nodejs' | 'php' | 'go'>('javascript');
    const [activeScenario, setActiveScenario] = useState<string>('simple');
    const [activeInspectorTab, setActiveInspectorTab] = useState<'html' | 'text' | 'raw'>('html');
    const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [copiedContent, setCopiedContent] = useState(false);
    const [currentWeek, setCurrentWeek] = useState('');

    useEffect(() => {
        const now = new Date();
        const start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        const format = (d: Date) => d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
        setCurrentWeek(`${format(start)} - ${format(now)}, ${now.getFullYear()}`);
    }, []);

    const handleCopyContent = (text: string) => {
        navigator.clipboard.writeText(text);
        setCopiedContent(true);
        setTimeout(() => setCopiedContent(false), 2000);
    };

    const adjustIframeHeight = () => {
        const iframe = iframeRef.current;
        if (iframe && iframe.contentWindow) {
            try {
                const body = iframe.contentWindow.document.body;
                const html = iframe.contentWindow.document.documentElement;
                const height = Math.max(body.scrollHeight, body.offsetHeight, html.clientHeight, html.scrollHeight, html.offsetHeight);
                iframe.style.height = height + 'px';
            } catch (e) { iframe.style.height = '600px'; }
        }
    };

    useEffect(() => {
        if (activeEmail && activeInspectorTab === 'html') {
            const timer = setTimeout(adjustIframeHeight, 100);
            return () => clearTimeout(timer);
        }
    }, [activeEmail, activeInspectorTab, previewMode]);

    const mailScenarios: Record<string, MailScenario> = {
        simple: {
            label: 'Welcome Signup',
            icon: <Mail size={14} />,
            payload: {
                to: 'newuser@example.com', from: 'welcome@filefusion.app', subject: 'Verify your account',
                text: 'Welcome to FileFusion! Your verification code is 882-941. Please enter this code to complete your registration.',
                html: `
<div style="font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #2d3748; background: #f8fafc; padding: 10px;">
    <div style="max-width: 600px; margin: 0 auto; background: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05); border: 1px solid #e2e8f0;">
        <div style="background: linear-gradient(135deg, #4299e1 0%, #3182ce 100%); padding: 32px 24px; color: white; text-align: center;">
            <div style="background: rgba(255, 255, 255, 0.2); width: 48px; height: 48px; border-radius: 12px; display: inline-flex; align-items: center; justify-content: center; margin-bottom: 16px; font-size: 24px; line-height: 48px;">👋</div>
            <h1 style="margin: 0; font-size: 24px; font-weight: 700;">Welcome to FileFusion!</h1>
            <p style="margin: 8px 0 0 0; opacity: 0.9; font-size: 14px;">We're excited to have you on board.</p>
        </div>
        <div style="padding: 32px 24px;">
            <p style="font-size: 16px; line-height: 1.6; margin-bottom: 24px; color: #4a5568;">To get started with your professional file management suite, please use the verification code below:</p>
            <div style="background: #edf2f7; padding: 24px; border-radius: 12px; text-align: center; margin-bottom: 32px;">
                <div style="font-size: 12px; font-weight: 600; color: #718096; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 8px;">Verification Code</div>
                <div style="font-family: 'Roboto Mono', monospace; font-size: 36px; font-weight: 700; color: #2d3748; letter-spacing: 0.1em;">882-941</div>
            </div>
            <div style="text-align: center;"><a href="https://filefusion.app/verify" style="display: inline-block; padding: 12px 28px; background: #3182ce; color: white; text-decoration: none; border-radius: 8px; font-weight: 600; font-size: 14px; box-shadow: 0 4px 12px rgba(49, 130, 206, 0.2);">Go to Verification Page</a></div>
            <p style="margin-top: 32px; font-size: 14px; color: #718096; text-align: center; border-top: 1px solid #edf2f7; padding-top: 24px;">Didn't sign up for FileFusion? You can safely ignore this email.</p>
        </div>
        <div style="background: #f1f5f9; padding: 12px; text-align: center; border-top: 1px solid #e2e8f0;"><p style="margin: 0; font-size: 11px; color: #64748b;">© 2026 FileFusion Inc. • The future of file handling.</p></div>
    </div>
</div>`
            }
        },
        premium: {
            label: 'Password Reset',
            icon: <Zap size={14} />,
            payload: {
                to: 'user@example.com', from: 'security@filefusion.app', subject: '⚠️ Password Reset Requested',
                text: 'Reset Your Password: We received a request to reset your password. If this was not you, please ignore this email. Link: https://filefusion.app/auth/reset',
                html: `
<div style="font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #2d3748; background: #f8fafc; padding: 10px;">
    <div style="max-width: 600px; margin: 0 auto; background: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05); border: 1px solid #e2e8f0;">
        <div style="background: #2d3748; padding: 24px; text-align: center;"><div style="display: inline-block; width: 40px; height: 40px; background: #1a73e8; border-radius: 10px; line-height: 40px; font-size: 20px;">🔒</div></div>
        <div style="padding: 32px 24px;">
            <h2 style="margin: 0 0 16px 0; font-size: 20px; font-weight: 700; color: #2d3748; text-align: center;">Password Reset Request</h2>
            <p style="font-size: 15px; line-height: 1.6; color: #4a5568; margin-bottom: 24px; text-align: center;">We received a request to reset your password for your FileFusion account.</p>
            <div style="text-align: center; margin-bottom: 32px;"><a href="https://filefusion.app/auth/reset" style="display: inline-block; padding: 14px 32px; background: #1a73e8; color: white; text-decoration: none; border-radius: 8px; font-weight: 600; font-size: 15px; box-shadow: 0 4px 10px rgba(26, 115, 232, 0.2);">Reset My Password</a></div>
            <div style="background: #fff5f5; border: 1px solid #fed7d7; padding: 16px; border-radius: 8px;"><p style="margin: 0; font-size: 13px; color: #c53030; line-height: 1.4;"><strong>Security Tip:</strong> This link will expire in 15 minutes.</p></div>
        </div>
        <div style="background: #f8fafc; padding: 20px; text-align: center; border-top: 1px solid #edf2f7;"><p style="margin: 0; font-size: 11px; color: #a0aec0;">Need help? <a href="#" style="color: #3182ce; text-decoration: none;">Contact FileFusion Support</a></p></div>
    </div>
</div>`
            }
        },
        image: {
            label: 'Weekly Analytics',
            icon: <ImageIcon size={14} />,
            payload: {
                to: 'admin@company.com', from: 'insights@filefusion.io', subject: 'Your Weekly Processing Report',
                text: 'Your Weekly Processing Report: You processed 1,240 documents this week using FileFusion AI. Great work! View your dashboard for more insights.',
                html: `
<div style="font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #2d3748; background: #f8fafc; padding: 10px;">
    <div style="max-width: 600px; margin: 0 auto; background: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05); border: 1px solid #e2e8f0;">
        <div style="background: #3182ce; padding: 20px 24px; color: white; text-align: left; position: relative;">
            <div style="position: absolute; top: 15px; right: 20px; opacity: 0.8; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em;">AI Powered Report</div>
            <h1 style="margin: 0; font-size: 20px; font-weight: 700;">Performance Insights</h1>
            <p style="margin: 4px 0 0 0; opacity: 0.9; font-size: 13px;">${currentWeek}</p>
        </div>
        <div style="padding: 0;"><img src="https://picsum.photos/seed/filefusion/600/250" style="width: 100%; height: auto; display: block;" alt="Performance Graph" /></div>
        <div style="padding: 24px;">
            <h2 style="color: #2b6cb0; margin: 0 0 12px 0; font-size: 18px; font-weight: 600;">Processing is up by 15%!</h2>
            <p style="font-size: 15px; line-height: 1.5; margin-bottom: 20px; color: #4a5568;">You processed <strong>1,240 documents</strong> this week using FileFusion AI.</p>
            <div style="background: #edf2f7; padding: 16px; border-radius: 10px; display: flex; align-items: center; justify-content: space-between;">
                <p style="margin: 0; font-size: 14px; color: #4a5568;"><span style="color: #3182ce;">★</span> Top Tool: <strong>PDF Optimizer</strong></p>
                <div style="font-size: 11px; font-weight: 600; color: #3182ce; background: #fff; padding: 4px 8px; border-radius: 6px;">FILEFUSION PRO</div>
            </div>
            <div style="margin-top: 24px; text-align: center;"><a href="https://filefusion.app/dashboard" style="display: inline-block; padding: 10px 24px; background: #3182ce; color: white; text-decoration: none; border-radius: 8px; font-weight: 600; font-size: 14px; box-shadow: 0 4px 10px rgba(49, 130, 206, 0.2);">Upgrade to FF+ for more insights</a></div>
        </div>
        <div style="background: #f1f5f9; padding: 12px; text-align: center; border-top: 1px solid #e2e8f0;"><p style="margin: 0; font-size: 11px; color: #64748b;">Sent via <a href="https://filefusion.app" style="color: #3182ce; text-decoration: none; font-weight: 600;">FileFusion Developer Hub</a></p></div>
    </div>
</div>`
            }
        }
    };

    const getSnippet = (lang: string, scenario: string) => {
        const payload = mailScenarios[scenario].payload;
        const formatHtml = (html: string | undefined, l: string) => {
            if (!html) return null;
            const trimmed = html.trim();
            if (l === 'python') return `"""${trimmed}"""`;
            if (l === 'php') return `<<<HTML\n${trimmed}\nHTML`;
            if (l === 'go' || l === 'javascript' || l === 'nodejs') return `\`${trimmed}\``;
            return `"${trimmed.replace(/"/g, '\\"')}"`;
        };
        const htmlPart = formatHtml(payload.html, lang);
        const contentField = payload.html ? 'html' : 'text';
        const contentValue = payload.html ? htmlPart : `"${payload.text}"`;

        if (lang === 'javascript') return `const payload = {\n  to: "${payload.to}",\n  from: "${payload.from}",\n  subject: "${payload.subject}",\n  ${contentField}: ${contentValue}\n};\n\nfetch('${sendUrl}', {\n  method: 'POST',\n  headers: { 'Content-Type': 'application/json' },\n  body: JSON.stringify(payload)\n})\n.then(res => res.json())\n.then(console.log);`;
        if (lang === 'python') return `import requests\n\nurl = "${sendUrl}"\npayload = {\n    "to": "${payload.to}",\n    "from": "${payload.from}",\n    "subject": "${payload.subject}",\n    "${contentField}": ${contentValue}\n}\n\nresponse = requests.post(url, json=payload)\nprint(response.json())`;
        if (lang === 'nodejs') return `const axios = require('axios');\n\nconst payload = {\n  to: "${payload.to}",\n  from: "${payload.from}",\n  subject: "${payload.subject}",\n  ${contentField}: ${contentValue}\n};\n\naxios.post('${sendUrl}', payload)\n.then(res => console.log(res.data))\n.catch(console.error);`;
        if (lang === 'php') return `<?php\n$url = "${sendUrl}";\n$payload = [\n    "to" => "${payload.to}",\n    "from" => "${payload.from}",\n    "subject" => "${payload.subject}",\n    "${contentField}" => ${contentValue === null ? 'null' : contentValue}\n];\n\n$ch = curl_init($url);\ncurl_setopt($ch, CURLOPT_RETURNTRANSFER, true);\ncurl_setopt($ch, CURLOPT_POST, true);\ncurl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($payload));\ncurl_setopt($ch, CURLOPT_HTTPHEADER, ['Content-Type: application/json']);\n\n$response = curl_exec($ch);\necho $response;\ncurl_close($ch);\n?>`;
        if (lang === 'go') return `package main\n\nimport (\n    "bytes"\n    "encoding/json"\n    "fmt"\n    "net/http"\n)\n\nfunc main() {\n    url := "${sendUrl}"\n    payload := map[string]interface{}{\n        "to":      "${payload.to}",\n        "from":    "${payload.from}",\n        "subject": "${payload.subject}",\n        "${contentField}":   ${contentValue},\n    }\n    \n    jsonData, _ := json.Marshal(payload)\n    resp, _ := http.Post(url, "application/json", bytes.NewBuffer(jsonData))\n    defer resp.Body.Close()\n    fmt.Println("Status:", resp.Status)\n}`;
        return '';
    };

    const [copiedUrl, setCopiedUrl] = useState(false);
    const [copiedSnippet, setCopiedSnippet] = useState(false);

    const handleCopyUrl = () => { navigator.clipboard.writeText(sendUrl); setCopiedUrl(true); setTimeout(() => setCopiedUrl(false), 2000); };
    const handleCopySnippet = () => { navigator.clipboard.writeText(getSnippet(activeTab, activeScenario)); setCopiedSnippet(true); setTimeout(() => setCopiedSnippet(false), 2000); };

    const sendTestEmail = async () => {
        if (isSendingTest) return;
        setIsSendingTest(true); setDispatchStatus(null);
        const payload = mailScenarios[activeScenario].payload;
        try {
            const res = await fetch(sendUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            if (res.ok) { setDispatchStatus('success'); fetchMail(); } else { setDispatchStatus('error'); }
        } catch (err) { console.error('Failed to send test email:', err); setDispatchStatus('error'); }
        finally { setIsSendingTest(false); setTimeout(() => setDispatchStatus(null), 3000); }
    };

    // ── Landing screen ──────────────────────────────────────────────────────
    if (!mailboxId) {
        return (
            <DevPageLayout
                title="Bulletproof Your Emails"
                subtitle="Capture, inspect, and debug transactional emails without spamming real inboxes."
                className="dev-mail-page"
            >
                <SEO title="Mail Sandbox | FileFusion" description="Test outbound emails with an instant, private sandbox. Capture and inspect HTML/text mail without real sending." />

                {/* .dev-panel .section-spacing */}
                <DevPanel className="mt-[32px] text-center px-[24px] py-[80px] flex flex-col items-center">
                    <div className="w-[80px] h-[80px] bg-[#e8f0fe] rounded-[20px] flex items-center justify-center mb-[24px] text-[#1a73e8]">
                        <Mail size={40} />
                    </div>
                    <h2 className="text-[1.75rem] font-medium text-[#202124] mb-[16px]">Transactional Email Sandbox</h2>
                    <p className="text-[#5f6368] text-[1.1rem] max-w-[600px] mx-auto mb-[40px] leading-relaxed">
                        Test your registration, password reset, and notification emails in a controlled environment.
                        FileFusion Mail Sandbox captures your outbound JSON payloads and renders them exactly as your users see them.
                    </p>
                    <button
                        className="bg-[#1a73e8] text-white border-none px-[40px] py-[14px] rounded-[12px] font-semibold text-[16px] flex items-center gap-[12px] cursor-pointer relative overflow-hidden transition-all duration-200 hover:bg-[#1557b0] hover:shadow-[0_8px_16px_rgba(26,115,232,0.2)] active:scale-95 group"
                        onClick={handleGenerateId}
                    >
                        <Mail size={20} /> Create My Sandbox ID
                        <div className="absolute top-0 -left-full w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-all duration-500 group-hover:left-full"></div>
                    </button>
                </DevPanel>

                <MailDocs />
            </DevPageLayout>
        );
    }

    // ── Active dashboard ─────────────────────────────────────────────────────
    return (
        <DevPageLayout
            title="Email Sandbox"
            subtitle="Point your application's email client to our API to capture and inspect transactional emails in real-time."
            className="dev-mail-page"
        >
            <SEO title="Mail Sandbox | FileFusion" description="Test outbound emails with an instant, private sandbox. Capture and inspect HTML/text mail without real sending." />

            {/* Integration Guide Panel — .dev-panel .section-spacing */}
            <DevPanel className="mt-[32px] shadow-[0_4px_16px_rgba(0,0,0,0.08)]">
                <div className="px-[24px] py-[16px] bg-gradient-to-br from-[#f8f9fa] to-white border-b border-[#dadce0] flex justify-between items-center gap-[20px]">
                    <div className="flex items-center gap-[12px]">
                        <Zap size={20} className="text-[#1a73e8]" />
                        <h3 className="m-0 text-[1.15rem] font-medium text-[#202124]">Interactive Integration Guide</h3>
                    </div>
                    <div className="flex items-center">
                        <button
                            className={`flex items-center gap-[10px] px-[20px] py-[10px] rounded-[8px] font-semibold text-[14px] text-white border-none cursor-pointer relative overflow-hidden transition-all duration-200 shadow-[0_2px_6px_rgba(26,115,232,0.2)] active:translate-y-0 ${isSendingTest ? 'opacity-80 cursor-wait bg-[#1a73e8]' : dispatchStatus === 'success' ? 'bg-[#28a745] shadow-[0_4px_12px_rgba(40,167,69,0.3)]' : dispatchStatus === 'error' ? 'bg-[#d93025] shadow-[0_4px_12px_rgba(220,53,69,0.3)]' : 'bg-[#1a73e8] hover:bg-[#1557b0] hover:shadow-[0_4px_12px_rgba(26,115,232,0.3)] hover:-translate-y-[1px]'} group`}
                            onClick={sendTestEmail}
                            disabled={isSendingTest}
                        >
                            {isSendingTest ? <RefreshCw size={16} className="animate-spin" /> : dispatchStatus === 'success' ? <Check size={16} /> : dispatchStatus === 'error' ? <Info size={16} /> : <Send size={16} />}
                            <span>{isSendingTest ? 'Dispatching...' : dispatchStatus === 'success' ? 'Sent Successfully' : dispatchStatus === 'error' ? 'Failed' : 'Dispatch Test Mail'}</span>
                            <div className="absolute top-0 -left-full w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-all duration-500 group-hover:left-full"></div>
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 p-0">
                    {/* Step 1: Scenario */}
                    <div className="px-[32px] py-[24px] flex flex-col gap-[16px] border-b border-[#dadce0]">
                        <div className="flex items-center gap-[12px]">
                            <div className="w-[28px] h-[28px] bg-[#1a73e8] text-white rounded-full flex items-center justify-center text-[14px] font-bold">1</div>
                            <div className="font-semibold text-[#202124] text-[15px]">Choose a Scenario</div>
                        </div>
                        <div className="flex flex-wrap gap-[12px]">
                            {Object.entries(mailScenarios).map(([id, s]) => (
                                <button
                                    key={id}
                                    className={`flex-1 min-w-[240px] max-w-[320px] min-h-[72px] flex items-center gap-[12px] p-[12px] border rounded-[12px] cursor-pointer transition-all duration-200 text-left relative hover:border-[#1a73e8] hover:shadow-[0_4px_12px_rgba(26,115,232,0.1)] hover:-translate-y-[2px] focus:outline-none ${activeScenario === id ? 'bg-[#e8f0fe] border-[#1a73e8]' : 'bg-white border-[#dadce0]'}`}
                                    onClick={() => setActiveScenario(id)}
                                >
                                    <div className={`w-[40px] h-[40px] rounded-[8px] flex items-center justify-center transition-colors duration-200 ${activeScenario === id ? 'bg-white text-[#1a73e8]' : 'bg-[#f1f3f4] text-[#80868b]'}`}>{s.icon}</div>
                                    <div className="flex flex-col flex-1">
                                        <span className="font-semibold text-[#202124] text-[14px]">{s.label}</span>
                                    </div>
                                    {activeScenario === id && <Check size={16} className="text-[#1a73e8]" />}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Step 2: Code snippet */}
                    <div className="px-[32px] py-[24px] flex flex-col gap-[20px]">
                        <div className="flex items-center gap-[12px]">
                            <div className="w-[28px] h-[28px] bg-[#1a73e8] text-white rounded-full flex items-center justify-center text-[14px] font-bold">2</div>
                            <div className="font-semibold text-[#202124] text-[15px]">Implement in your Code</div>
                        </div>

                        <div className="bg-white border border-[#dadce0] rounded-[12px] overflow-hidden flex flex-col max-w-full">
                            <div className="flex justify-between items-center pr-[8px] pl-[16px] bg-[#f8f9fa] border-b border-[#dadce0]">
                                <div className="flex">
                                    {(['javascript', 'python', 'nodejs', 'php', 'go'] as const).map(lang => (
                                        <button
                                            key={lang}
                                            className={`px-[16px] py-[12px] bg-transparent border-none border-b-2 text-[12px] font-semibold uppercase cursor-pointer transition-colors duration-200 ${activeTab === lang ? 'text-[#1a73e8] border-[#1a73e8]' : 'text-[#80868b] border-transparent hover:text-[#202124]'}`}
                                            onClick={() => setActiveTab(lang)}
                                        >{lang}</button>
                                    ))}
                                </div>
                                <div className="flex-1"></div>
                                <button
                                    className={`w-[32px] h-[32px] flex items-center justify-center rounded-[6px] cursor-pointer transition-colors duration-200 ${copiedSnippet ? 'bg-[#e6f4ea] text-[#1e8e3e] border border-[#ceead6]' : 'bg-transparent text-[#5f6368] border-none hover:bg-[#e8eaed] hover:text-[#202124]'}`}
                                    onClick={handleCopySnippet}
                                    title={copiedSnippet ? 'Copied!' : 'Copy Code'}
                                >
                                    {copiedSnippet ? <Check size={16} /> : <Copy size={16} />}
                                </button>
                            </div>

                            <div className="h-[400px] overflow-y-auto overflow-x-hidden bg-white rounded-b-[12px] [&_pre]:whitespace-pre-wrap [&_pre]:break-all [&_code]:whitespace-pre-wrap [&_code]:break-all [&_.code-block-container]:m-0 [&_.code-block-container]:border-none [&_.code-block-container]:shadow-none">
                                <CodeBlock language={activeTab === 'nodejs' ? 'javascript' : activeTab} code={getSnippet(activeTab, activeScenario)} variant="light" showLanguage={false} showCopy={false} />
                            </div>
                        </div>
                    </div>
                </div>
            </DevPanel>

            {/* Sandbox URL Panel — .dev-panel .section-spacing */}
            <DevPanel className="mt-[32px] shadow-[0_4px_16px_rgba(0,0,0,0.08)]">
                <div className="p-[32px]">
                    <h4 className="text-[16px] font-semibold mb-[16px] text-[#202124]">Your Unique Sandbox URL</h4>
                    <p className="text-[#5f6368] text-[14px] mb-[24px] leading-relaxed">
                        Point your application's email client to this host to capture and inspect transactional emails in real-time.
                    </p>
                    <div className="flex items-stretch bg-white border border-[#dadce0] rounded-[8px] overflow-hidden transition-all duration-200 hover:border-[#1a73e8] hover:shadow-[0_4px_12px_rgba(26,115,232,0.08)]">
                        <div className="bg-[#e8f0fe] text-[#1a73e8] px-[16px] py-[12px] font-bold text-[12px] border-r border-[#dadce0] flex items-center justify-center min-w-[80px]">POST</div>
                        <div className="flex-[1] px-[16px] py-[12px] font-mono text-[13px] text-[#5f6368] whitespace-pre-wrap break-all flex items-center">{sendUrl}</div>
                        <div className="flex-1"></div>
                        <button
                            className={`w-[48px] flex items-center justify-center cursor-pointer transition-colors duration-200 border-none ${copiedUrl ? 'bg-[#e6f4ea] text-[#1e8e3e]' : 'bg-transparent text-[#5f6368] hover:bg-[#e8eaed] hover:text-[#202124]'}`}
                            onClick={handleCopyUrl}
                            title={copiedUrl ? 'Copied!' : 'Copy URL'}
                        >
                            {copiedUrl ? <Check size={16} /> : <Copy size={16} />}
                        </button>
                    </div>
                </div>
            </DevPanel>

            {/* Inbox + Inspector Grid */}
            <div className="mt-[32px] grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-[24px] items-start min-h-[520px]">
                {/* Sidebar: Inbox */}
                <div className="flex flex-col bg-white border border-[#9aa0a6] rounded-[12px] shadow-[0_4px_16px_rgba(0,0,0,0.08)] overflow-hidden h-[520px] sticky top-[80px] z-10">
                    <div className="bg-[#f8f9fa] px-[16px] py-[10px] border-b border-[#dadce0] flex justify-between items-center h-[52px]">
                        <h3 className="m-0 text-[1rem] font-semibold text-[#202124]">Inbox</h3>
                        <div className="flex items-center gap-[8px]">
                            <div className={`px-[8px] py-[4px] rounded-full text-[10px] font-bold uppercase tracking-wide flex items-center gap-[4px] mr-[4px] ${isPolling ? 'bg-[#e6f4ea] text-[#1e8e3e]' : 'bg-[#fce8e6] text-[#d93025]'}`}>
                                {isPolling ? 'LIVE' : 'PAUSED'}
                            </div>
                            <button
                                className={`w-[28px] h-[28px] rounded-[6px] flex items-center justify-center transition-colors duration-200 cursor-pointer border-none ${!isPolling ? 'bg-[#fef7e0] text-[#ea8600] hover:bg-[#fce8e6] hover:text-[#d93025]' : 'bg-transparent text-[#1a73e8] hover:bg-[#e8eaed] hover:text-[#202124]'}`}
                                onClick={() => setIsPolling(!isPolling)}
                                title={isPolling ? 'Pause Monitoring' : 'Resume Monitoring'}
                            >
                                {isPolling ? <Pause size={16} /> : <Play size={16} />}
                            </button>
                            <button className="w-[28px] h-[28px] rounded-[6px] flex items-center justify-center transition-colors duration-200 cursor-pointer border-none bg-transparent text-[#d93025] hover:bg-[#fce8e6]" onClick={clearInbox} title="Clear history">
                                <Trash2 size={16} />
                            </button>
                        </div>
                    </div>

                    <div className="p-0 flex flex-col overflow-hidden h-[calc(100%-52px)]">
                        <div className="px-[16px] py-[12px] border-b border-[#dadce0] flex items-center gap-[10px] bg-white">
                            <Search size={16} className="text-[#80868b]" />
                            <input
                                type="text"
                                className="w-full bg-[#f1f3f4] border border-transparent rounded-[8px] px-[12px] py-[8px] text-[14px] text-[#202124] transition-all duration-200 focus:outline-none focus:bg-white focus:border-[#1a73e8] focus:shadow-[0_0_0_3px_#e8f0fe] placeholder-[#80868b]"
                                placeholder="Search inbox..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <div className="flex-1 overflow-y-auto bg-white min-h-0 h-full [&::-webkit-scrollbar]:w-[6px] [&::-webkit-scrollbar-thumb]:bg-[#dadce0] [&::-webkit-scrollbar-thumb]:rounded-[3px]">
                            {filteredEmails.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-full p-[48px] text-center bg-[#f8f9fa] border-2 border-dashed border-[#dadce0] rounded-[16px] m-[16px]">
                                    <Mail size={48} className="text-[#dadce0] mb-[16px]" />
                                    <p className="text-[#5f6368] font-medium m-0 text-[15px]">{searchTerm ? 'No matches found.' : 'Your inbox is empty.'}</p>
                                </div>
                            ) : (
                                filteredEmails.map(mail => (
                                    <div
                                        key={mail.id}
                                        className={`px-[16px] py-[10px] border-b cursor-pointer transition-all duration-200 hover:bg-[#f8f9fa] ${activeEmail?.id === mail.id ? 'bg-[#e8f0fe] border-b-[#dadce0] border-l-[4px] border-l-[#1a73e8] pl-[12px]' : 'bg-transparent border-b-[#dadce0] border-l-[4px] border-l-transparent pl-[12px]'}`}
                                        onClick={() => setActiveEmail(mail)}
                                    >
                                        <div className="flex justify-between mb-[2px]">
                                            <span className="font-semibold text-[13px] text-[#202124] overflow-hidden text-ellipsis whitespace-nowrap">{mail.from}</span>
                                            <span className="text-[11px] text-[#80868b]">{formatTime(mail.timestamp)}</span>
                                        </div>
                                        <div className="text-[13px] text-[#5f6368] font-medium mb-[1px] overflow-hidden text-ellipsis whitespace-nowrap">{mail.subject}</div>
                                        <div className="text-[12px] text-[#80868b] overflow-hidden text-ellipsis whitespace-nowrap leading-[1.4]">{mail.text?.substring(0, 60)}...</div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>

                {/* Email Inspector */}
                <div className="bg-white overflow-hidden border border-[#9aa0a6] rounded-[12px] shadow-[0_4px_16px_rgba(0,0,0,0.08)] h-auto min-h-0">
                    {activeEmail ? (
                        <div className="flex flex-col h-auto">
                            <div className="bg-[#f8f9fa] px-[20px] border-b border-[#dadce0] flex justify-between items-center min-h-[52px]">
                                <div className="flex gap-[8px]">
                                    <button className={`px-[16px] py-[12px] bg-transparent border-none border-b-2 text-[13px] font-medium flex items-center gap-[8px] cursor-pointer transition-all duration-200 hover:bg-black/5 focus:outline-none ${activeInspectorTab === 'html' ? 'text-[#1a73e8] border-b-[#1a73e8]' : 'text-[#80868b] border-b-transparent hover:text-[#202124]'}`} onClick={() => setActiveInspectorTab('html')}><Eye size={14} /> View HTML</button>
                                    <button className={`px-[16px] py-[12px] bg-transparent border-none border-b-2 text-[13px] font-medium flex items-center gap-[8px] cursor-pointer transition-all duration-200 hover:bg-black/5 focus:outline-none ${activeInspectorTab === 'text' ? 'text-[#1a73e8] border-b-[#1a73e8]' : 'text-[#80868b] border-b-transparent hover:text-[#202124]'}`} onClick={() => setActiveInspectorTab('text')}><Info size={14} /> Text</button>
                                    <button className={`px-[16px] py-[12px] bg-transparent border-none border-b-2 text-[13px] font-medium flex items-center gap-[8px] cursor-pointer transition-all duration-200 hover:bg-black/5 focus:outline-none ${activeInspectorTab === 'raw' ? 'text-[#1a73e8] border-b-[#1a73e8]' : 'text-[#80868b] border-b-transparent hover:text-[#202124]'}`} onClick={() => setActiveInspectorTab('raw')}><Code size={14} /> Payload</button>
                                </div>
                                <div className="flex items-center gap-[12px]">
                                    {(activeInspectorTab === 'text' || activeInspectorTab === 'raw') && (
                                        <button
                                            className={`w-[32px] h-[32px] flex items-center justify-center rounded-[6px] cursor-pointer transition-colors duration-200 border-none ${copiedContent ? 'bg-[#e6f4ea] text-[#1e8e3e] shadow-none' : 'bg-transparent text-[#5f6368] hover:bg-[#e8eaed] hover:text-[#202124]'}`}
                                            onClick={() => handleCopyContent(activeInspectorTab === 'text' ? activeEmail.text : JSON.stringify(activeEmail, null, 2))}
                                            title={copiedContent ? 'Copied' : 'Copy to Clipboard'}
                                        >
                                            {copiedContent ? <Check size={16} /> : <Copy size={16} />}
                                        </button>
                                    )}
                                    {activeInspectorTab === 'html' && (
                                        <div className="flex gap-[8px]">
                                            <button className={`p-[6px] border rounded-[6px] cursor-pointer transition-all duration-200 ${previewMode === 'desktop' ? 'bg-[#1a73e8] text-white border-[#1a73e8]' : 'bg-white text-[#5f6368] border-[#dadce0] hover:border-[#1a73e8] hover:text-[#1a73e8]'}`} onClick={() => setPreviewMode('desktop')}><Monitor size={14} /></button>
                                            <button className={`p-[6px] border rounded-[6px] cursor-pointer transition-all duration-200 ${previewMode === 'mobile' ? 'bg-[#1a73e8] text-white border-[#1a73e8]' : 'bg-white text-[#5f6368] border-[#dadce0] hover:border-[#1a73e8] hover:text-[#1a73e8]'}`} onClick={() => setPreviewMode('mobile')}><Smartphone size={14} /></button>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="flex-1 overflow-visible bg-[#f1f3f4]">
                                {activeInspectorTab === 'html' ? (
                                    <div className={`h-auto p-0 overflow-visible flex justify-center bg-[#f8fafc] ${previewMode === 'mobile' ? 'py-[32px] px-[16px]' : ''}`}>
                                        <div className={`bg-white shadow-[0_4px_20px_rgba(0,0,0,0.08)] h-auto max-w-full transition-all duration-300 ease-in-out ${previewMode === 'mobile' ? 'w-[375px] border-[12px] border-[#2d3748] rounded-[32px] min-h-[600px] overflow-hidden' : 'w-full'}`}>
                                            <iframe ref={iframeRef} title="Email Preview" srcDoc={activeEmail.html || `<p>${activeEmail.text}</p>`} className="w-full h-full border-none" onLoad={adjustIframeHeight} />
                                        </div>
                                    </div>
                                ) : activeInspectorTab === 'text' ? (
                                    <div className="p-[24px] bg-[#f8fafc] min-h-[400px]">
                                        <div className="bg-white border border-[#dadce0] rounded-[12px] shadow-[0_2px_8px_rgba(0,0,0,0.04)] overflow-hidden mt-[8px]">
                                            <div className="p-[24px] text-[15px] leading-[1.7] text-[#202124] whitespace-pre-wrap break-normal break-words font-sans">{activeEmail.text}</div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="p-[16px] bg-[#f1f3f4] [&_.code-block-container]:m-0 [&_.code-block-container]:border [&_.code-block-container]:border-[#dadce0] [&_.code-block-container]:shadow-[0_2px_10px_rgba(0,0,0,0.05)] [&_pre]:whitespace-pre-wrap [&_pre]:break-all [&_code]:whitespace-pre-wrap [&_code]:break-all">
                                        <CodeBlock language="json" code={JSON.stringify(activeEmail, null, 2)} variant="light" showLanguage={false} showCopy={false} />
                                    </div>
                                )}
                            </div>
                            <div className="py-[16px] px-[24px] bg-white border-t border-[#dadce0] flex flex-wrap gap-[24px]">
                                <div className="text-[13px] flex gap-[8px]"><span className="text-[#80868b]">From:</span> <span className="font-semibold text-[#202124]">{activeEmail.from}</span></div>
                                <div className="text-[13px] flex gap-[8px]"><span className="text-[#80868b]">To:</span> <span className="font-semibold text-[#202124]">{activeEmail.to}</span></div>
                                <div className="text-[13px] flex gap-[8px]"><span className="text-[#80868b]">Subject:</span> <span className="font-semibold text-[#202124]">{activeEmail.subject}</span></div>
                            </div>
                        </div>
                    ) : (
                        <div className="h-[520px] flex flex-col justify-center items-center border border-[#dadce0] rounded-[12px] text-center bg-[#f8f9fa] m-[16px]">
                            <Eye size={32} className="text-[#dadce0] mb-[16px]" />
                            <h3 className="m-0 text-[1.25rem] font-semibold text-[#202124] mb-[8px]">Email Inspector</h3>
                            <p className="text-[#5f6368] font-medium m-0 text-[15px]">Select an email from the inbox to inspect its content.</p>
                        </div>
                    )}
                </div>
            </div>

            <MailDocs />
        </DevPageLayout>
    );
};

export default DevMailPage;
